import { useState } from "react";
import {
  QUESTION_TYPES,
  STAR_FORMULAS,
  IMPORTANCE_AGREEMENT_FORMULAS,
  CHOICE_FORMULAS,
  MULTI_FORMULAS,
  DEFAULT_SCORING_BY_TYPE,
} from "./questionTypeConfig";

/**
 * Per-question-type scoring formula builder.
 */
export default function ScoringFormulaBuilder({ value, onChange }) {
  const config = { ...DEFAULT_SCORING_BY_TYPE, ...(value || {}) };
  const [openType, setOpenType] = useState("rating");

  const setTypeConfig = (type, patch) => {
    onChange?.({
      ...config,
      [type]: { ...(config[type] || DEFAULT_SCORING_BY_TYPE[type]), ...patch },
    });
  };

  const formulasFor = (type) => {
    if (type === "rating") return STAR_FORMULAS;
    if (type === "importance_agreement") return IMPORTANCE_AGREEMENT_FORMULAS;
    if (type === "multiple_choice") return MULTI_FORMULAS;
    if (type === "dropdown" || type === "single_choice") return CHOICE_FORMULAS;
    return [];
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="px-3 py-2.5 border-b border-slate-100">
        <p className="text-sm font-semibold text-slate-800">Scoring by question type</p>
        <p className="text-xs text-slate-500 mt-0.5">
          Pick how each type turns an answer into a number for reports.
        </p>
      </div>

      <div className="flex flex-wrap gap-1 px-2 pt-2">
        {QUESTION_TYPES.map((t) => {
          const active = openType === t.value;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setOpenType(t.value)}
              className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                active
                  ? "bg-[#004F73] text-white border-[#004F73]"
                  : "bg-white text-slate-600 border-slate-200 hover:border-[#004F73]/40"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {QUESTION_TYPES.map((t) => {
        if (openType !== t.value) return null;
        const row = config[t.value] || DEFAULT_SCORING_BY_TYPE[t.value];
        const formulas = formulasFor(t.value);

        return (
          <div key={t.value} className="p-3 space-y-3">
            {!t.scorable ? (
              <p className="text-sm text-slate-600 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                Open-ended answers are text-only — they are not scored.
              </p>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Formula
                  </label>
                  <select
                    value={row.formula || formulas[0]?.value}
                    onChange={(e) => setTypeConfig(t.value, { formula: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white"
                  >
                    {formulas.map((f) => (
                      <option key={f.value} value={f.value}>
                        {f.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-400 mt-1">
                    {formulas.find((f) => f.value === row.formula)?.hint || formulas[0]?.hint || ""}
                  </p>
                </div>

                <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={row.include_in_aggregate !== false}
                    onChange={(e) =>
                      setTypeConfig(t.value, { include_in_aggregate: e.target.checked })
                    }
                  />
                  <span>
                    <span className="font-medium">Count in overall assessment score</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      If checked, answers of this type roll into the company/team total. If
                      unchecked, they stay available for reporting but do not change the overall
                      score.
                    </span>
                  </span>
                </label>

                {(t.value === "dropdown" ||
                  t.value === "single_choice" ||
                  t.value === "multiple_choice") && (
                  <p className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
                    Set each option&apos;s <strong>weight</strong> next to its label when you add
                    or edit the question (e.g. Poor = 1, Excellent = 5). The formula above uses
                    those weights.
                  </p>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}
